import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import ServiceSection from "@/components/ServiceSection";
import SEO from "@/components/SEO";
import FAQSection from "@/components/FAQSection";
import { 
  GraduationCap, 
  Video, 
  Palette, 
  Camera, 
  Globe, 
  MessageSquare,
  Lightbulb,
  Clock,
  Target,
  Zap,
  Play
} from "lucide-react";

const TrainingPage = () => {
  const services = [
    {
      icon: Lightbulb,
      title: "Strategy & Content Planning",
      description: "We audit your existing training, identify gaps, and create a comprehensive content strategy. Our instructional design expertise ensures every piece of content serves a clear learning objective.",
      services: [
        "Training needs analysis",
        "Learning journey mapping",
        "Content gap identification",
        "Instructional design",
        "KPI and measurement planning"
      ],
      image: "https://customer-assets.emergentagent.com/job_2f219309-49d2-4096-9b1f-9aa2825973b1/artifacts/sp0djlei_IMG_2629.jpg",
      altText: "Training strategy and content planning session for employee onboarding programme"
    },
    {
      icon: Video,
      title: "Video Production",
      description: "From complex process demonstrations to engaging onboarding welcomes, we produce training videos that people actually watch. Professional quality that reflects your brand standards.",
      services: [
        "Onboarding video series",
        "Process and procedure guides",
        "Software training tutorials",
        "Expert interview content",
        "Animated explainers"
      ],
      image: "https://customer-assets.emergentagent.com/job_2f219309-49d2-4096-9b1f-9aa2825973b1/artifacts/d60i35nx_IMG_1388.JPG",
      altText: "Professional video production for employee training and onboarding content"
    },
    {
      icon: Palette,
      title: "Graphic Design",
      description: "Supporting materials that reinforce learning. Quick-reference cards, infographics, and visual guides that employees can access when they need a reminder.",
      services: [
        "Training workbooks and guides",
        "Quick-reference cards",
        "Process infographics",
        "Digital learning assets",
        "Certificate and badge design"
      ],
      image: "https://customer-assets.emergentagent.com/job_2f219309-49d2-4096-9b1f-9aa2825973b1/artifacts/wuoqyjsm_print%20direct%20mail%20design.jpg",
      altText: "Graphic design and print materials for corporate training communications"
    },
    {
      icon: Camera,
      title: "Professional Photography",
      description: "Staff know when imagery is fake. We replace cold, impersonal stock photos with real photography of your people and your sites — building trust and familiarity across every piece of training content.",
      services: [
        "Equipment documentation",
        "Step-by-step process shots",
        "Facility and location imagery",
        "Before and after comparisons",
        "Employee demonstration photos"
      ],
      image: "https://customer-assets.emergentagent.com/job_2f219309-49d2-4096-9b1f-9aa2825973b1/artifacts/0pv4maqg_582%20low%20res.jpg",
      altText: "Real employee photography for training materials replacing stock imagery"
    },
    {
      icon: MessageSquare,
      title: "Content Creation",
      description: "Engaging copy that makes training accessible. From script writing to assessment questions, we create content that educates without boring.",
      services: [
        "Video scriptwriting",
        "Assessment and quiz creation",
        "Training documentation",
        "FAQ and knowledge base content",
        "Accessibility considerations"
      ],
      image: "https://customer-assets.emergentagent.com/job_2f219309-49d2-4096-9b1f-9aa2825973b1/artifacts/wa19h2g6_content%20creation.jpg",
      altText: "Digital content creation for employee training and development programmes"
    },
    {
      icon: Globe,
      title: "Website & Portal Development",
      description: "Dedicated training portals and LMS integrations that make content accessible anywhere, anytime. Track completion, measure comprehension, and identify needs.",
      services: [
        "Training portal development",
        "LMS integration support",
        "Mobile-responsive design",
        "Progress tracking systems",
        "Interactive learning modules"
      ],
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800",
      altText: "E-learning platform and training portal development for large organisations"
    }
  ];

  const benefits = [
    {
      icon: Clock,
      title: "Faster Onboarding",
      description: "Reduce time-to-productivity with engaging, structured training content that new hires actually retain."
    },
    {
      icon: Target,
      title: "Consistent Quality",
      description: "Every employee receives the same high-quality training, regardless of location or trainer availability."
    },
    {
      icon: Zap,
      title: "Innovation Ready",
      description: "When processes change, update your training quickly without starting from scratch."
    }
  ];

  return (
    <div data-testid="training-page" className="min-h-screen bg-[#050505]">
      <SEO
        title="Employee Training Videos & Onboarding Content Production"
        description="Professional training video production and e-learning content for large organisations. Reduce onboarding time, improve knowledge retention and deliver consistent training across every site and department."
        keywords="training videos, employee training content, onboarding videos, e-learning production, training video production, internal communications training, corporate training content, staff onboarding, employee development, training for large companies"
        canonical="/training"
      />
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center" data-testid="training-hero">
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_2f219309-49d2-4096-9b1f-9aa2825973b1/artifacts/7po2xrqn_training.jpg"
            alt="Employee training video presentation being delivered to staff in large organisation"
            className="w-full h-full object-cover"
            decoding="async"
            fetchpriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/90 to-transparent" />
          <div className="absolute inset-0 hero-overlay" />
        </div>

        <div className="container mx-auto px-4 md:px-8 relative z-10 pt-32">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-lg bg-[#00E5FF]/20 flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-[#00E5FF]" />
              </div>
              <span className="text-[#00E5FF] font-semibold uppercase tracking-wider">
                Internal Communications
              </span>
            </div>

            <h1 className="font-display text-5xl md:text-7xl tracking-tight leading-none text-white mb-6">
              TRAINING
            </h1>

            <p className="text-lg md:text-xl text-[#B3B3B3] max-w-2xl mb-8">
              Your training resources probably already exist — on shared drives, intranets 
              and folders nobody opens. The problem isn't a lack of materials, it's that 
              nobody can find them or wants to use them. We make training content that's 
              findable, engaging and impossible to ignore.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button
                  data-testid="training-book-call-btn"
                  className="bg-[#00E5FF] text-black hover:bg-[#00B8CC] font-bold px-8 py-6 uppercase tracking-wider text-base"
                >
                  <Play className="w-5 h-5 mr-2 fill-black" />
                  Book a Call
                </Button>
              </Link>
              <Link to="/case-studies">
                <Button
                  data-testid="training-case-studies-btn"
                  variant="outline"
                  className="bg-white/10 text-white hover:bg-white/20 border-white/10 px-8 py-6 uppercase tracking-wider text-base"
                >
                  View Case Studies
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="py-10 md:py-16 bg-[#0a0a0a]" data-testid="training-problem">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-display text-3xl md:text-5xl tracking-wide text-white mb-8">
              OUTDATED TRAINING COSTS YOU
              <br />
              <span className="text-[#00E5FF]">TIME, MONEY, AND TALENT</span>
            </h2>
            <p className="text-[#B3B3B3] text-lg leading-relaxed mb-8">
              Paper manuals nobody reads. Lengthy classroom sessions that interrupt 
              productivity. Inconsistent knowledge transfer from busy managers. New 
              starters left to figure things out themselves. Sound familiar?
            </p>
            <p className="text-white text-xl font-medium">
              Modern training demands modern content. Video-based learning, 
              accessible documentation, and scalable delivery that works for your 
              business - not against it.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-10 md:py-16 bg-[#050505]" data-testid="training-benefits">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="p-8 bg-[#141414] rounded-lg border border-[#1F1F1F] hover:border-[#00E5FF]/30 transition-colors"
                data-testid={`benefit-${index}`}
              >
                <div className="w-14 h-14 rounded-lg bg-[#00E5FF]/10 flex items-center justify-center mb-6">
                  <benefit.icon className="w-7 h-7 text-[#00E5FF]" />
                </div>
                <h3 className="font-display text-xl tracking-wide text-white mb-3">
                  {benefit.title}
                </h3>
                <p className="text-[#B3B3B3]">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-10 md:py-16 bg-[#0a0a0a]" data-testid="training-services">
        <div className="container mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <h2 className="font-display text-4xl md:text-5xl tracking-wide text-white mb-4">
              WHAT WE DELIVER
            </h2>
            <p className="text-[#B3B3B3] text-lg max-w-2xl mx-auto">
              Complete training solutions from strategy to execution, designed to 
              make learning accessible, engaging, and effective.
            </p>
          </div>

          <div className="space-y-20">
            {services.map((service, index) => (
              <ServiceSection
                key={index}
                {...service}
                reverse={index % 2 === 1}
                accentColor="#00E5FF"
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <FAQSection faqs={[
        { question: "What types of training videos do you produce?", answer: "We produce onboarding videos, process demonstrations, safety training, compliance content, soft skills training, leadership messaging and e-learning modules. Every video is tailored to your organisation and filmed with your real people and environments." },
        { question: "How do training videos improve employee onboarding?", answer: "Video-based onboarding delivers consistent messaging to every new starter regardless of location or start date. It reduces the burden on managers, shortens time-to-competence and gives new employees an engaging introduction to your culture, processes and expectations." },
        { question: "Why is professional training content better than DIY?", answer: "Professional production ensures your training content is clear, engaging and credible. Poorly produced content undermines the message and reflects badly on your organisation. We combine broadcast-quality production with instructional design expertise to create content people actually want to watch." },
        { question: "Can training content be accessed on mobile devices?", answer: "Yes. All content we create is optimised for multi-device delivery. We can also build dedicated training portals and integrate with your existing LMS to ensure content is accessible anywhere, anytime — particularly important for frontline and remote workers." }
      ]} />

      <section className="py-20 md:py-32 bg-[#050505] relative overflow-hidden" data-testid="training-cta">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#00E5FF] rounded-full blur-[150px]" />
        </div>
        
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-display text-4xl md:text-5xl tracking-wide text-white mb-6">
              READY TO TRANSFORM
              <br />
              <span className="text-[#00E5FF]">YOUR TRAINING?</span>
            </h2>
            <p className="text-[#B3B3B3] text-lg mb-8">
              Let's discuss how we can help your team learn faster, retain more, 
              and perform better.
            </p>
            <Link to="/contact">
              <Button
                data-testid="training-cta-btn"
                className="bg-[#00E5FF] text-black hover:bg-[#00B8CC] font-bold px-10 py-6 uppercase tracking-wider text-base"
              >
                Start Your Training Transformation
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TrainingPage;
